import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UINotificationBadge from 'UIComponents/badge/UINotificationBadge';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import HistoryPanelHeaderWithLink from 'automation-ui-canvas/containers/enrollment-history/panel/HistoryPanelHeaderWithLink';
import DateFilterSection from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/DateFilterSection';
import StepFilterSelect from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/StepFilterSelect';
import VersionAndDateFilterSection from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/VersionAndDateFilterSection';
import { useSkippedEnrollmentsList } from 'automation-ui-canvas/containers/enrollment-history/panel/hooks/skippedEnrollmentHooks';
import { SKIPPED_ENROLLMENT_LIST_FIELDNAME } from 'automation-ui-canvas/containers/enrollment-history/panel/queries/skippedEnrollmentQueries';
import { getEnrollmentHistoryListUrl, getMetricsUrl, getSkippedEnrollmentsListUrl } from 'automation-ui-canvas/utils/urls';
import { getIsObjectlessFlow } from 'automation-ui-flow-core/flow/flowUtils';
import ObjectFilterSelect from 'automation-ui-log-events/components/ObjectFilterSelect';
import { ApplicationHistoryFilterKeys } from 'automation-ui-management/constants/ApplicationHistoryFilterKeys';
import { useApplicationHistoryFilters } from 'automation-ui-management/hooks/filterHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { useHistory, useLocation } from 'react-router-dom';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EnrollmentsListFilterSection = ({
  flow,
  isHybrid,
  isSkippedEnrollmentsScreen
}) => {
  const {
    filters,
    updateFilters
  } = useApplicationHistoryFilters();
  const selectedObjectId = filters.getObjectId();
  const trackEvent = useTrackEvent();
  const history = useHistory();
  const {
    search
  } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isVersionFilteringEnabled = searchParams.get('showVersions') === 'true';
  const {
    data: {
      [SKIPPED_ENROLLMENT_LIST_FIELDNAME]: skippedEnrollmentsResponse = {}
    } = {},
    loading
  } = useSkippedEnrollmentsList({
    flowId: flow.flowId,
    objectTypeId: flow.objectTypeId,
    hybrid: !!isHybrid,
    skip: isSkippedEnrollmentsScreen
  });
  const {
    results: skippedEnrollmentsList = []
  } = skippedEnrollmentsResponse;
  const handleObjectIdChange = ({
    target: {
      value
    }
  }) => {
    const objectId = value || undefined;
    updateFilters({
      [ApplicationHistoryFilterKeys.OBJECT_ID]: objectId
    });
    trackEvent(Trackables.PERFORMANCE_HISTORY_FILTERS_RECORD, {
      objectId
    });
  };
  const handleClearFilters = hasVersionFilters => {
    history.replace({
      pathname: isSkippedEnrollmentsScreen ? getSkippedEnrollmentsListUrl({
        flowId: flow.flowId
      }) : getEnrollmentHistoryListUrl({
        flowId: flow.flowId
      }),
      search: hasVersionFilters ? '?showVersions=true' : ''
    });
  };
  const navigateToMetrics = () => {
    trackEvent(Trackables.PERFORMANCE_HISTORY_VIEW_METRICS);
    searchParams.delete('actions');
    history.push({
      pathname: getMetricsUrl({
        flowId: flow.flowId
      }),
      search: searchParams.toString()
    });
  };
  const navigateToSkippedEnrollments = () => {
    trackEvent(Trackables.PERFORMANCE_HISTORY_VIEW_SKIPPED_ENROLLMENTS);
    history.replace({
      pathname: getSkippedEnrollmentsListUrl({
        flowId: flow.flowId
      }),
      search: searchParams.toString()
    });
  };
  const navigateToEnrollmentsList = () => {
    trackEvent(Trackables.PERFORMANCE_HISTORY_CLICKED_CHANGE_ENROLLMENT);
    history.replace({
      pathname: getEnrollmentHistoryListUrl({
        flowId: flow.flowId
      }),
      search: searchParams.toString()
    });
  };
  return /*#__PURE__*/_jsxs(UIPanelSection, {
    className: "p-all-0",
    children: [/*#__PURE__*/_jsxs(View, {
      padding: "500",
      borderBlockEndWidth: "200",
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-default",
      backgroundColor: "fill-tertiary-default",
      children: [/*#__PURE__*/_jsx(HistoryPanelHeaderWithLink, {
        titleKey: isSkippedEnrollmentsScreen ? 'automation.enrollmentHistory.panel.skipped-enrollments.header.title' : 'automation.enrollmentHistory.panel.enrollment-list.header.title',
        navigationLinkKey: isSkippedEnrollmentsScreen ? 'automation.enrollmentHistory.panel.skipped-enrollments.header.navigationLink' : 'automation.enrollmentHistory.panel.enrollment-list.header.navigationLink',
        navigationLinkClickHandler: () => isSkippedEnrollmentsScreen ? navigateToEnrollmentsList() : navigateToMetrics(),
        navigationDirection: isSkippedEnrollmentsScreen ? 'left' : 'right'
      }), /*#__PURE__*/_jsx(UICheckbox, {
        checked: isVersionFilteringEnabled,
        onChange: ({
          target: {
            checked
          }
        }) => {
          handleClearFilters(checked);
          trackEvent(Trackables.PERFORMANCE_HISTORY_TOGGLE_FILTER_BY_WORKFLOW_VERSIONS);
        },
        className: "m-bottom-2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.enrollmentHistory.panel.enrollment-list.filter.filterByWorkflowVersions"
        })
      }), isVersionFilteringEnabled ? /*#__PURE__*/_jsx(VersionAndDateFilterSection, {
        flowId: flow.flowId,
        page: "enrollment-history"
      }) : /*#__PURE__*/_jsx(DateFilterSection, {}), isVersionFilteringEnabled && !isSkippedEnrollmentsScreen && /*#__PURE__*/_jsx(StepFilterSelect, {
        flowId: flow.flowId
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        align: "center",
        className: "p-top-1 m-right-1",
        children: [!loading && skippedEnrollmentsList.length > 0 && !isSkippedEnrollmentsScreen && /*#__PURE__*/_jsx(UINotificationBadge, {
          size: "small",
          placement: "inline",
          badgeClassName: "m-left-1",
          badgeDescription: I18n.text('automation.enrollmentHistory.panel.enrollment-list.filter.viewSkippedEnrollments'),
          children: /*#__PURE__*/_jsx(UILink, {
            onClick: () => navigateToSkippedEnrollments(),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.enrollmentHistory.panel.enrollment-list.filter.viewSkippedEnrollments"
            })
          })
        }), /*#__PURE__*/_jsx(UIFlex, {
          justify: "end",
          children: /*#__PURE__*/_jsx(UILink, {
            onClick: () => {
              handleClearFilters(isVersionFilteringEnabled);
              trackEvent(Trackables.PERFORMANCE_HISTORY_FILTERS_CLEAR);
            },
            "data-test-id": "enrollment-history-clear-filters",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.enrollmentHistory.panel.enrollment-list.filter.resetFilters"
            })
          })
        })]
      })]
    }), !getIsObjectlessFlow(flow) && /*#__PURE__*/_jsx("div", {
      className: "m-left-10 m-y-5",
      children: /*#__PURE__*/_jsx(ObjectFilterSelect, {
        flowObjectTypeId: flow.objectTypeId,
        updateUrlFilters: handleObjectIdChange,
        selectedObjectId: selectedObjectId,
        "data-test-id": "enrollment-history-object-filter",
        menuWidth: 420
      })
    })]
  });
};
export default EnrollmentsListFilterSection;